import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Send, Plus, X } from 'lucide-react';
import type { WebsiteName } from '@/types/website';
import { WEBSITE_LABELS } from '@/types/website';

interface NoteInputProps {
  onSubmit: (note: { note: string; website: WebsiteName; labels: string[] }) => void;
  defaultWebsite?: WebsiteName;
  isLoading?: boolean;
  suggestedLabels?: string[];
}

export function NoteInput({ onSubmit, defaultWebsite, isLoading, suggestedLabels = [] }: NoteInputProps) {
  const [note, setNote] = useState('');
  const [website, setWebsite] = useState<WebsiteName>(defaultWebsite || 'greenvision');
  const [labels, setLabels] = useState<string[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [showLabels, setShowLabels] = useState(false);

  const handleSubmit = () => {
    if (!note.trim()) return;
    onSubmit({ note: note.trim(), website, labels });
    setNote('');
    setLabels([]);
    setShowLabels(false);
  };

  const addLabel = (label: string) => {
    if (label.trim() && !labels.includes(label.trim())) {
      setLabels([...labels, label.trim()]);
      setNewLabel('');
    }
  };

  const removeLabel = (label: string) => {
    setLabels(labels.filter((l) => l !== label));
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Schrijf een notitie..."
          rows={3}
          className="resize-none"
        />

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Select value={website} onValueChange={(v) => setWebsite(v as WebsiteName)}>
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(WEBSITE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setShowLabels(!showLabels)}
            >
              <Plus className="h-3 w-3 mr-1" />
              Labels
            </Button>
          </div>

          <Button onClick={handleSubmit} disabled={!note.trim() || isLoading} size="sm">
            <Send className="h-4 w-4 mr-1" />
            Plaatsen
          </Button>
        </div>

        {showLabels && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex gap-2">
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Nieuw label"
                className="h-8 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLabel(newLabel))}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => addLabel(newLabel)}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>

            {suggestedLabels.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {suggestedLabels
                  .filter((l) => !labels.includes(l))
                  .slice(0, 5)
                  .map((label) => (
                    <Badge
                      key={label}
                      variant="outline"
                      className="text-xs cursor-pointer hover:bg-muted"
                      onClick={() => addLabel(label)}
                    >
                      + {label}
                    </Badge>
                  ))}
              </div>
            )}

            {labels.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {labels.map((label) => (
                  <Badge key={label} variant="secondary" className="text-xs gap-1">
                    {label}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => removeLabel(label)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
